/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7a: the rules and the createProduct payload behind the New program guide.
 */
import ProductObj from "../Common/ProductObj.js";
import * as PRODUCTS from "@lemonadetraining/web-shared/constants/ProductConst.js";
import * as PRICING from "@lemonadetraining/web-shared/constants/Pricing.js";
import * as PROGRAMMING from "@lemonadetraining/web-shared/constants/ProgrammingConst.js";
import { callableCode } from "./callableError";

export const NAME_MIN = 3;
export const NAME_MAX = 100;
export const DESCRIPTION_MAX = 500;
export const WEEKS_MIN = 1;
// TimelineControl refuses a 13th week.
export const WEEKS_MAX = 12;
// Stripe cannot charge less than €0.50, so a lower price could never be bought.
export const PRICE_MIN = 0.5;

export type StepKey = "who" | "name" | "runs" | "price" | "cover";
export type RunsAs = "reoccurring" | "timeline" | "fix";
export type PriceKind = "free" | "subscription" | "one time";
export type PaymentsState = "checking" | "ready" | "not-connected" | "unknown";

export type Field =
  "isPT" | "name" | "description" | "runsAs" | "weeks" | "priceKind" | "amount" | "cover" | "terms";

export type FieldErrors = Partial<Record<Field, string>>;

export interface NewProgramDraft {
  isPT: boolean;
  name: string;
  description: string;
  runsAs: RunsAs | null;
  weeks: string;
  priceKind: PriceKind;
  amount: string;
  coverArtURL: string | null;
  coverUploading: boolean;
  termsAccepted: boolean;
}

export const initialDraft = (isPT: boolean): NewProgramDraft => ({
  isPT,
  name: "",
  description: "",
  runsAs: null,
  weeks: "4",
  priceKind: PRICING.TYPE.free as PriceKind,
  amount: "",
  coverArtURL: null,
  coverUploading: false,
  termsAccepted: false,
});

/** The gym question only makes sense inside a gym; an independent coach answers PT on the name step. */
export const stepsFor = (isBoxContext: boolean): StepKey[] =>
  isBoxContext ? ["who", "name", "runs", "price", "cover"] : ["name", "runs", "price", "cover"];

/** Field order inside each step: the first one with an error takes focus. */
export const FIELD_ORDER: Field[] = [
  "isPT",
  "name",
  "description",
  "runsAs",
  "weeks",
  "priceKind",
  "amount",
  "cover",
  "terms",
];

export const firstInvalidField = (errors: FieldErrors): Field | null =>
  FIELD_ORDER.find((field) => errors[field]) ?? null;

/** Euros from what a person typed: "19,90" and "19.90" both work. Null when it is not a price. */
export const parseAmount = (raw: string): number | null => {
  const text = raw.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
};

export const parseWeeks = (raw: string): number | null => {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return null;
  const value = Number(text);
  return value >= WEEKS_MIN && value <= WEEKS_MAX ? value : null;
};

export const isPaid = (kind: PriceKind) => kind !== PRICING.TYPE.free;

interface ValidationContext {
  payments: PaymentsState;
}

export const validateStep = (
  step: StepKey,
  draft: NewProgramDraft,
  { payments }: ValidationContext
): FieldErrors => {
  const errors: FieldErrors = {};
  switch (step) {
    case "who":
      if (typeof draft.isPT !== "boolean") errors.isPT = "Choose who the program is for.";
      break;
    case "name": {
      const name = draft.name.trim();
      if (!name) errors.name = "Give the program a name.";
      else if (name.length < NAME_MIN)
        errors.name = `Use at least ${NAME_MIN} characters for the name.`;
      else if (name.length > NAME_MAX)
        errors.name = `Keep the name to ${NAME_MAX} characters or fewer.`;
      if (draft.description.trim().length > DESCRIPTION_MAX)
        errors.description = `Keep this to ${DESCRIPTION_MAX} characters or fewer.`;
      break;
    }
    case "runs":
      if (!draft.runsAs) errors.runsAs = "Choose how the program runs.";
      else if (draft.runsAs === PROGRAMMING.TYPE.timeline && parseWeeks(draft.weeks) === null)
        errors.weeks = `Enter a whole number of weeks from ${WEEKS_MIN} to ${WEEKS_MAX}.`;
      break;
    case "price":
      if (
        ![PRICING.TYPE.free, PRICING.TYPE.oneTime, PRICING.TYPE.subscription].includes(
          draft.priceKind
        )
      )
        errors.priceKind = "Choose a price.";
      else if (isPaid(draft.priceKind)) {
        if (payments !== "ready") {
          errors.priceKind = "Payments aren't connected yet, so choose Free for now.";
        } else {
          const amount = parseAmount(draft.amount);
          if (amount === null) errors.amount = "Enter a price in euros, for example 19,90.";
          else if (amount < PRICE_MIN) errors.amount = "The lowest price is 0,50 euros.";
        }
      }
      break;
    case "cover":
      if (draft.coverUploading) errors.cover = "Wait for the picture to finish uploading.";
      if (!draft.termsAccepted) errors.terms = "Agree to the terms to create the program.";
      break;
    default:
      break;
  }
  return errors;
};

/** Every step at once, for the final check before creating. Returns the first failing step. */
export const validateAll = (
  steps: StepKey[],
  draft: NewProgramDraft,
  context: ValidationContext
): { step: StepKey; errors: FieldErrors } | null => {
  for (const step of steps) {
    const errors = validateStep(step, draft, context);
    if (Object.keys(errors).length > 0) return { step, errors };
  }
  return null;
};

export interface ProgramCreator {
  uid: string;
  displayName: string;
  hasLemonadeRights: boolean;
  /** The account the payments check found; ignored for platform admins, as the old wizard did. */
  connectedAccountID: string | null;
  boxID?: string | null;
}

/**
 * The same ProductObj the old CreateProgrammingWizard sent. Differences are values only:
 * no cover sends images [] and coverArtURL null (as memberships do), an empty description
 * is left out (Stripe refuses an empty one), and a week-by-week plan carries `weeks`
 * (Product.weeks in the contract; TimelineControl writes the same field later).
 */
export const buildProgramProduct = (draft: NewProgramDraft, creator: ProgramCreator) => {
  const product = new ProductObj(
    draft.name.trim(),
    PRODUCTS.TYPE.Programming,
    creator.hasLemonadeRights
  ) as ProductObj & Record<string, unknown>;

  const paid = isPaid(draft.priceKind);
  const description = draft.description.trim();

  Object.assign(product, {
    description,
    pricing: { type: draft.priceKind, amount: paid ? (parseAmount(draft.amount) ?? 0) : 0 },
    images: draft.coverArtURL ? [draft.coverArtURL] : [],
    coverArtURL: draft.coverArtURL,
    stripeAccountID: creator.hasLemonadeRights ? null : creator.connectedAccountID,
    ownerName: creator.displayName,
    ownerUID: creator.uid,
    CoachName: creator.displayName,
    CoachUID: creator.uid,
    programmingType: { type: draft.runsAs },
    isPT: draft.isPT,
  });

  if (!description) delete product.description;
  if (draft.runsAs === PROGRAMMING.TYPE.timeline) product.weeks = parseWeeks(draft.weeks);
  if (creator.boxID) {
    product.boxID = creator.boxID;
    product.programType = "box";
  }
  return product;
};

// Thrown by createProduct's pre-checks, before any Stripe or Firestore write.
const REFUSED_BEFORE_WRITING = [
  "permission-denied",
  "failed-precondition",
  "invalid-argument",
  "unauthenticated",
  "not-found",
];

/** A plain sentence for a failed create. Only a pre-check refusal is known to have created nothing. */
export const createFailureMessage = (error: unknown): string => {
  const code = callableCode(error);
  if (code && REFUSED_BEFORE_WRITING.includes(code)) {
    const nothingCreated = "Nothing was created, so you can fix this and try again.";
    switch (code) {
      case "permission-denied":
        return `You don't have permission to create programs here. Ask a gym admin for access. ${nothingCreated}`;
      case "failed-precondition":
        return `Your settings don't allow this program yet: programs may be turned off for the gym, or payments aren't connected for a paid program. ${nothingCreated}`;
      case "unauthenticated":
        return `You've been signed out. Sign in again, then create the program. ${nothingCreated}`;
      default:
        return `Something in the form wasn't accepted. Check each step and try again. ${nothingCreated}`;
    }
  }
  return "We couldn't confirm the program was created. Check your program list before trying again, so you don't end up with two.";
};
